import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
    Box,
    TableCell,
    TableRow,
    CircularProgress,
    Alert,
    Snackbar,
    IconButton,
    Tooltip,
    Rating,
    ToggleButton,
    ToggleButtonGroup,
    Chip,
    Popover,
    Divider,
    Typography,
} from '@mui/material';
import { AppButton } from '../../../shared/components/AppButton';
import { AppTable } from '../../../shared/components/AppTable';
import { AppTablePagination } from '../../../shared/components/AppTablePagination';
import { Delete as DeleteIcon, Refresh as RefreshIcon, List as ListIcon, GridView as GroupIcon } from '@mui/icons-material';
import { getProductRatings, deleteProductRating } from '../../../shared/services/productRatingService';
import { ProductRating as ProductRatingType } from '../../../shared/types/productRating';

interface GroupedRating {
    productId: number;
    totalUsers: number;
    averageRating: number;
    ratingSum: number;
}

export const ProductRating: React.FC = () => {
    const [viewMode, setViewMode] = useState<'individual' | 'grouped'>('grouped');
    const [ratings, setRatings] = useState<ProductRatingType[]>([]);
    const [loading, setLoading] = useState<boolean>(false);
    const [error, setError] = useState<string>('');
    const [success, setSuccess] = useState<string>('');
    const [currentPage, setCurrentPage] = useState(1);
    const [pageSize, setPageSize] = useState(10);
    const [pagination, setPagination] = useState<{
        currentPage: number;
        totalPages: number;
        totalItems: number;
        itemsPerPage: number;
    } | null>(null);
    const [deleteAnchorEl, setDeleteAnchorEl] = useState<HTMLButtonElement | null>(null);
    const [selectedRatingId, setSelectedRatingId] = useState<string | null>(null);

    const loadRatings = useCallback(async () => {
        try {
            setLoading(true);
            setError('');
            const response = await getProductRatings({
                page: currentPage,
                limit: pageSize,
            });
            if (response.success && response.data) {
                setRatings(response.data.data);
                setPagination(response.data.pagination);
            } else {
                setRatings([]);
                setPagination(null);
            }
        } catch (err: unknown) {
            const message = err && typeof err === 'object' && 'response' in err
                ? (err as { response?: { data?: { message?: string } } }).response?.data?.message
                : undefined;
            setError(message || 'Failed to load ratings');
            setRatings([]);
            setPagination(null);
        } finally {
            setLoading(false);
        }
    }, [currentPage, pageSize]);

    useEffect(() => {
        loadRatings();
    }, [loadRatings]);

    const groupedRatings = useMemo(() => {
        const groups: Record<number, GroupedRating> = {};
        ratings.forEach(r => {
            if (!groups[r.productId]) {
                groups[r.productId] = {
                    productId: r.productId,
                    totalUsers: 0,
                    averageRating: 0,
                    ratingSum: 0
                };
            }
            groups[r.productId].totalUsers += 1;
            groups[r.productId].ratingSum += r.rating;
        });

        return Object.values(groups).map(g => ({
            ...g,
            averageRating: Number((g.ratingSum / g.totalUsers).toFixed(1))
        }));
    }, [ratings]);

    const handleDeleteClick = (event: React.MouseEvent<HTMLButtonElement>, id: string) => {
        setDeleteAnchorEl(event.currentTarget);
        setSelectedRatingId(id);
    };

    const handleDeleteClose = () => {
        setDeleteAnchorEl(null);
        setSelectedRatingId(null);
    };

    const handleDeleteConfirm = async () => {
        if (!selectedRatingId) return;

        try {
            setLoading(true);
            const response = await deleteProductRating(selectedRatingId);
            if (response.success) {
                setSuccess(response.message || 'Rating deleted successfully');
                await loadRatings();
            } else {
                setError(response.message || 'Failed to delete rating');
            }
        } catch (err: unknown) {
            const message = err && typeof err === 'object' && 'response' in err
                ? (err as { response?: { data?: { message?: string } } }).response?.data?.message
                : undefined;
            setError(message || 'Failed to delete rating');
        } finally {
            setLoading(false);
            handleDeleteClose();
        }
    };

    const handleCloseSnackbar = () => {
        setError('');
        setSuccess('');
    };

    const handlePageChange = (_: React.ChangeEvent<unknown>, newPage: number) => {
        setCurrentPage(newPage);
    };

    const handleViewModeChange = (
        _event: React.MouseEvent<HTMLElement>,
        newMode: 'individual' | 'grouped' | null,
    ) => {
        if (newMode !== null) {
            setViewMode(newMode);
        }
    };

    const totalRows = pagination?.totalItems ?? ratings.length;

    return (
        <div className="flex flex-col flex-auto min-w-0">
            {/* Header */}
            <div className="flex flex-col sm:flex-row flex-0 sm:items-center sm:justify-between p-6 sm:py-8 sm:px-10 border-b bg-card dark:bg-transparent">
                <div className="flex-1 min-w-0">
                    <h2 className="text-3xl md:text-4xl font-extrabold tracking-tight leading-tight">
                        Product Ratings
                    </h2>
                </div>
                <div className="flex items-center gap-4 mt-4 sm:mt-0 flex-wrap">
                    <ToggleButtonGroup
                        value={viewMode}
                        exclusive
                        onChange={handleViewModeChange}
                        aria-label="view mode"
                        size="small"
                        className="bg-white"
                    >
                        <ToggleButton value="grouped" aria-label="grouped">
                            <Tooltip title="Grouped by Product">
                                <GroupIcon />
                            </Tooltip>
                        </ToggleButton>
                        <ToggleButton value="individual" aria-label="individual">
                            <Tooltip title="Individual Ratings">
                                <ListIcon />
                            </Tooltip>
                        </ToggleButton>
                    </ToggleButtonGroup>

                    <AppButton
                        variant="secondary"
                        startIcon={<RefreshIcon />}
                        onClick={loadRatings}
                        disabled={loading}
                    >
                        Refresh
                    </AppButton>
                </div>
            </div>

            {/* Content Area */}
            <div className="flex flex-auto p-6 sm:p-10">
                <div className="flex flex-col flex-auto min-w-0 w-full">
                    {loading && ratings.length === 0 ? (
                        <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
                            <CircularProgress />
                        </Box>
                    ) : ratings.length === 0 ? (
                        <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
                            <Alert severity="info">
                                No ratings found.
                            </Alert>
                        </Box>
                    ) : viewMode === 'grouped' ? (
                        <>
                            <AppTable
                                head={
                                    <TableRow className="bg-gray-100">
                                        <TableCell className="!font-semibold !text-gray-700">Product ID</TableCell>
                                        <TableCell className="!font-semibold !text-gray-700" align="center">Total Users</TableCell>
                                        <TableCell className="!font-semibold !text-gray-700" align="center">Average Rating</TableCell>
                                    </TableRow>
                                }
                                isEmpty={groupedRatings.length === 0}
                                emptyColSpan={3}
                                emptyMessage="No grouped data available."
                            >
                                {groupedRatings.map((group) => (
                                    <TableRow key={group.productId} hover>
                                        <TableCell className="font-medium">Product #{group.productId}</TableCell>
                                        <TableCell align="center">{group.totalUsers} users</TableCell>
                                        <TableCell align="center">
                                            <Box display="flex" alignItems="center" justifyContent="center" gap={1}>
                                                <Rating value={group.averageRating} readOnly precision={0.1} size="small" />
                                                <span className="text-sm font-bold">({group.averageRating})</span>
                                            </Box>
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </AppTable>
                        </>
                    ) : (
                        <>
                            <AppTable
                                head={
                                    <TableRow className="bg-gray-100">
                                        <TableCell className="!font-semibold !text-gray-700">User</TableCell>
                                        <TableCell className="!font-semibold !text-gray-700">Product ID</TableCell>
                                        <TableCell className="!font-semibold !text-gray-700">Rating</TableCell>
                                        <TableCell className="!font-semibold !text-gray-700">Created At</TableCell>
                                        <TableCell className="!font-semibold !text-gray-700" width={100} align="center">Actions</TableCell>
                                    </TableRow>
                                }
                                isEmpty={ratings.length === 0}
                                emptyColSpan={5}
                                emptyMessage="No ratings in this page."
                            >
                                {ratings.map((rating) => (
                                    <TableRow key={rating.id} hover>
                                        <TableCell>
                                            <div className="flex flex-col">
                                                <span className="font-medium text-gray-900">{rating.userName || 'N/A'}</span>
                                                <span className="text-xs text-gray-500">{rating.userId}</span>
                                            </div>
                                        </TableCell>
                                        <TableCell>{rating.productId}</TableCell>
                                        <TableCell>
                                            <Rating value={rating.rating} readOnly size="small" />
                                        </TableCell>
                                        <TableCell>
                                            {new Date(rating.createdAt).toLocaleString()}
                                        </TableCell>
                                        <TableCell align="center">
                                            <Tooltip title="Delete Rating">
                                                <IconButton
                                                    color="error"
                                                    onClick={(e) => handleDeleteClick(e, rating.id)}
                                                    size="small"
                                                >
                                                    <DeleteIcon />
                                                </IconButton>
                                            </Tooltip>
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </AppTable>
                        </>
                    )}

                    {/* Pagination is always shown for individual records or even grouped if we assume grouping happens per page */}
                    {viewMode === 'individual' && (
                        <AppTablePagination
                            totalRows={totalRows}
                            page={currentPage - 1}
                            pageSize={pageSize}
                            onPageChange={handlePageChange}
                            onPageSizeChange={(size) => {
                                setPageSize(size);
                                setCurrentPage(1);
                            }}
                        />
                    )}
                </div>
            </div>

            {/* Delete Confirmation Popover */}
            <Popover
                open={Boolean(deleteAnchorEl)}
                anchorEl={deleteAnchorEl}
                onClose={handleDeleteClose}
                anchorOrigin={{
                    vertical: 'bottom',
                    horizontal: 'right',
                }}
                transformOrigin={{
                    vertical: 'top',
                    horizontal: 'right',
                }}
                PaperProps={{
                    sx: {
                        p: 0,
                        mt: 1.5,
                        width: 250,
                        boxShadow: '0 10px 15px -3px rgb(0 0 0 / 0.1), 0 4px 6px -4px rgb(0 0 0 / 0.1)',
                        border: '1px solid #e5e7eb',
                        borderRadius: '12px',
                    },
                }}
            >
                <div className="p-4 bg-white">
                    <Typography className="font-semibold text-gray-900" sx={{ mb: 1 }}>
                        Are you sure?
                    </Typography>
                    <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
                        This rating will be permanently deleted.
                    </Typography>
                    <Divider sx={{ my: 1.5 }} />
                    <div className="flex justify-end gap-2">
                        <AppButton
                            variant="secondary"
                            size="small"
                            onClick={handleDeleteClose}
                        >
                            Cancel
                        </AppButton>
                        <AppButton
                            variant="danger"
                            size="small"
                            onClick={handleDeleteConfirm}
                        >
                            Delete
                        </AppButton>
                    </div>
                </div>
            </Popover>

            <Snackbar
                open={!!error || !!success}
                autoHideDuration={6000}
                onClose={handleCloseSnackbar}
                anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
            >
                {error ? (
                    <Alert onClose={handleCloseSnackbar} severity="error" sx={{ width: '100%' }}>
                        {error}
                    </Alert>
                ) : (
                    <Alert onClose={handleCloseSnackbar} severity="success" sx={{ width: '100%' }}>
                        {success}
                    </Alert>
                )}
            </Snackbar>
        </div>
    );
};
